import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack, type Theme } from "@mui/material";
import { startCase } from "lodash";

import { type PlacementCardDetails } from "./PlacementCard";

interface CardEventHeaderProps {
  iconType: "bolt-filled" | "resume" | "clock";
  label: string;
  color: (theme: Theme) => string | undefined;
  backgroundColor: (theme: Theme) => string | undefined;
}

function formatAvailabilityStrings(availability: Array<{ dayOfWeek: string }>): string {
  const availabilityStrings = availability.map((avail) => {
    const day = avail.dayOfWeek.toLowerCase();
    const processedDay = availability.length > 1 ? day.slice(0, 3) : day;
    return startCase(processedDay);
  });

  if (availabilityStrings.length === 1) {
    return availabilityStrings[0];
  }

  if (availabilityStrings.length === 2) {
    return `${availabilityStrings[0]} - ${availabilityStrings[1]}`;
  }

  const lastElement = availabilityStrings.pop()!;
  return `${availabilityStrings.join(", ")} & ${lastElement}`;
}

function getPlacementCardEventHeader(
  placement: PlacementCardDetails,
  startDates: string[] | undefined
): CardEventHeaderProps {
  // 1. Hiring Event Details (takes precedence)
  if (isDefined(startDates) && startDates.length > 0) {
    let interviewDaysLabel = "";
    if (isDefined(placement.interviewSettings) && placement.interviewSettings.scheduleInterviews) {
      const formattedDays = formatAvailabilityStrings(placement.interviewSettings.availability);
      interviewDaysLabel = ` & every ${formattedDays}`;
    }

    return {
      iconType: "bolt-filled" as const,
      label: `Interviewing ${startDates.join(", ")} ${interviewDaysLabel}`.trim(),
      color: (theme: Theme) => theme.palette.placement?.openHouse.text,
      backgroundColor: (theme: Theme) => theme.palette.placement?.openHouse.feature.background,
    };
  }

  // 2. Interview Setting Details
  if (
    isDefined(placement.interviewSettings) &&
    placement.interviewSettings.scheduleInterviews &&
    placement.interviewSettings.availability.length > 0
  ) {
    const formattedDays = formatAvailabilityStrings(placement.interviewSettings.availability);

    return {
      iconType: "bolt-filled" as const,
      label: `Interviewing every ${formattedDays}`,
      color: (theme: Theme) => theme.palette.text.primary,
      backgroundColor: (theme: Theme) => theme.palette.placement?.interviewDay.feature.background,
    };
  }

  // 3. Join waitlist
  if (placement.status === "waitlist") {
    return {
      iconType: "clock" as const,
      label: "Join the waitlist",
      color: (theme: Theme) => theme.palette.icon?.primary,
      backgroundColor: (theme: Theme) => theme.palette.border?.primary,
    };
  }

  return {
    iconType: "resume" as const,
    label: "Only accepting applications",
    color: (theme: Theme) => theme.palette.icon?.primary,
    backgroundColor: (theme: Theme) => theme.palette.border?.primary,
  };
}

interface PlacementCardEventHeaderProps {
  placement: PlacementCardDetails;
  startDates: string[] | undefined;
}

export function PlacementCardEventHeader(props: PlacementCardEventHeaderProps) {
  const { placement, startDates } = props;
  const eventHeaderDetails = getPlacementCardEventHeader(placement, startDates);

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          backgroundColor: eventHeaderDetails.backgroundColor,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 28,
          width: 28,
        }}
      >
        <CbhIcon
          size="xSmall"
          color={eventHeaderDetails.color}
          type={eventHeaderDetails.iconType}
        />
      </Box>
      <Text semibold variant="body2" color={eventHeaderDetails.color}>
        {eventHeaderDetails.label}
      </Text>
    </Stack>
  );
}
